import { useCallback, useEffect, useRef } from "react"
import type { RunExclusive } from "features/Provenance"

/**
 * Runs a request on the console's session once nothing else runs there: the console sends its
 * scripts and codebook reads straight to the engine, with `busy` set while one is under way (an
 * async script until its result is fetched). The engine does not serialise the calls of one
 * session, so a read of the lineage sent during a run mixes their answers or stops it.
 *
 * It waits only for what the console marks as busy: a run started while the request is in
 * flight is not held back (the lineage window covers the console's buttons while it reads).
 */
export const useRunWhenIdle = (busy: boolean): RunExclusive => {
  // Set in render, so a request made in the same commit as a run starts sees it.
  const busyRef = useRef(busy)
  busyRef.current = busy
  const waiting = useRef<(() => void)[]>([])
  useEffect(() => {
    if (!busy) waiting.current.splice(0).forEach((go) => go())
  }, [busy])
  return useCallback(
    <T,>(task: () => Promise<T>): Promise<T> =>
      busyRef.current ? new Promise<void>((go) => waiting.current.push(go)).then(task) : task(),
    []
  )
}
